import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useParams, useNavigate } from "react-router-dom";
import pie_chart from "../../assets/icons/pie-chart.svg";
import saddle from "../../assets/icons/saddle.svg";
import horse from "../../assets/icons/horse.svg";
import box from "../../assets/images/box.png";
import CloseIcon from "../../assets/icons/actions/close.svg";
import saddle_logo from "../../assets/icons/saddleLogo.svg";
import { FaUser, FaUsers, FaFolderOpen } from "react-icons/fa";
import { useUserContext } from "../../context/UserContext";
import useMyFileGrants from "../../hooks/useMyFileGrants";
import { useQuery } from "@apollo/client";
import { LIST_MANUFACTURERS_BASIC } from "../../apollo/queries/listManufacturers";

const toEncoded = (s = '') => encodeURIComponent(s?.toString().trim() || '')
const toKebab = (s = "") => s.toString().trim().toLowerCase().replace(/\s+/g, "-");

function MobileSideBar({ toggle, setToggle }) {
  const params = useParams();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const location = useLocation();
  const { userData, isManufacturerAdmin, isSaddlefitAdmin, isCactusAdmin, isTeamMember, isPrivateViewer } = useUserContext()
  const { hasGrants } = useMyFileGrants()

  const { data, loading, error } = useQuery(LIST_MANUFACTURERS_BASIC, {
    fetchPolicy: "network-only",
  });
  const manufacturers = data?.listManufacturers ?? [];

  const ordersSubmenu = useMemo(
    () =>
      manufacturers.map((m) => ({
        id: m?.id ?? toKebab(m?.brand ?? "unknown"),
        links: m?.brand ?? "Unknown",
        img: saddle,
        route: `/orders/${toEncoded(m?.brand)}`,
      })),
    [manufacturers]
  );

  const firstBrandRoute = ordersSubmenu[0]?.route || "";
  const userBrandRoute = useMemo(() => {
    const brand = userData?.brand || "";
    return brand ? `/orders/${toEncoded(brand)}` : (firstBrandRoute || "#");
  }, [userData?.brand, firstBrandRoute]);

  const trackingOrderRoute = `/order/${params?.id ? encodeURIComponent(params.id) : ""}`;
  const ordersRoutes = useMemo(
    () => [trackingOrderRoute || "", ...ordersSubmenu.map((s) => s.route)].filter(Boolean),
    [ordersSubmenu, trackingOrderRoute]
  );

  const [isOrdersSubmenuOpen, setIsOrdersSubmenuOpen] = useState(
    ordersRoutes.includes(location.pathname)
  );

  useEffect(() => {
    if (ordersRoutes.includes(location.pathname)) setIsOrdersSubmenuOpen(true);
  }, [location.pathname, ordersRoutes]);

  const handleOrdersClick = (e) => {
    if (!firstBrandRoute) return;
    e.preventDefault();
    setIsOrdersSubmenuOpen(true);
    if (location.pathname !== firstBrandRoute) navigate(firstBrandRoute);
    setToggle(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setToggle(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setToggle]);

  const pathName = `/saddles/manage/${encodeURIComponent(params?.encodedId || "")}`;
  const updateHorseRoute = `/horse-admin/horse/edit/${params?.horseId || ""}`;
  const horseOwnershipRoute = `/horse-admin/horse/transferOwnership/${params?.horseId || ""}`;

  const ordersAdditionalRoutes = useMemo(
    () => [...ordersSubmenu.map((s) => s.route)].filter(Boolean),
    [ordersSubmenu]
  );

  const event_links = [
    ...(isManufacturerAdmin || isCactusAdmin || isTeamMember
      ? [
        { links: "Dashboard", img: pie_chart, route: "/admin-dashboard" },
        { links: "Saddles", img: saddle, route: "/saddle-admin" },
        { links: 'Banner', img: box, route: '/banner' },
        ...(!isTeamMember ? [{ links: 'Team Members', Icon: FaUsers, route: '/team-members' }] : []),
        ...(isCactusAdmin || (isTeamMember && userData?.brand === "cactus")
          ? [
            {
              links: 'Custom Saddle Orders',
              img: saddle,
              route: '/custom-saddle-orders',
            },
          ]
          : []),
        {
          links: "Orders",
          img: box,
          route: userBrandRoute,
          directRoute: userBrandRoute,
        },
        ...(hasGrants ? [{ links: "Private Files", Icon: FaFolderOpen, route: "/file-browser" }] : []),
      ]
      : isPrivateViewer
        ? [
          { links: "Dashboard", img: pie_chart, route: "/admin-dashboard" },
          { links: "Private Files", Icon: FaFolderOpen, route: "/file-browser" },
        ]
      : isSaddlefitAdmin
        ? [
          { links: "Dashboard", img: pie_chart, route: "/admin-dashboard" },
          { links: "Event Creation", img: saddle, route: "/event-management" },
          {
            links: "Manufacturers",
            Icon: FaUser,
            additionalRoutes: [
              "/manufacturers/create",
              params?.id ? `/manufacturers/${encodeURIComponent(params.id)}` : "",
            ].filter(Boolean),
            route: "/manufacturers",
          },
          {
            links: "Saddle Admin",
            img: saddle,
            additionalRoutes: ["/saddles/manage", pathName],
            route: "/saddle-admin",
          },
          {
            links: "Horses Admin",
            img: horse,
            route: "/horse-admin",
            additionalRoutes: [updateHorseRoute, horseOwnershipRoute],
          },
          {
            links: 'Custom Saddle Orders',
            img: saddle,
            route: '/custom-saddle-orders',
          },
          {
            links: "Orders",
            img: box,
            route: firstBrandRoute || "#",
            additionalRoutes: ordersAdditionalRoutes,
            subMenu: ordersSubmenu,
          },
          { links: "Banner Section", img: box, route: "/banner-section" },
          { links: "File Browser", Icon: FaFolderOpen, route: "/file-browser" },
        ]
        : []),
  ];

  return (
    <div
      ref={sidebarRef}
      className={`fixed top-0 left-0 w-[300px] z-30 h-[100%] bg-[#2b364b] ${toggle ? "sidebar-slide-in" : "sidebar-slide-out"
        }`}
    >
      <div className="absolute top-5 right-5" onClick={() => setToggle(!toggle)}>
        <img src={CloseIcon} alt="Close menu" className="cursor-pointer border-none xl:hidden flex" />
      </div>

      <div className="flex items-center justify-center w-[90%] mt-[80px]">
        <img src={saddle_logo} alt="saddle_logo" className="w-[150px]" />
      </div>

      <div className="mt-[20px] w-full flex-1 overflow-y-auto mx-auto">
        {event_links.map((event, index) => {
          const additionalRoutes = Array.isArray(event.additionalRoutes)
            ? event.additionalRoutes
            : [];

          const isOrdersLink = event.links === "Orders";
          const isActive = isOrdersLink
            ? location.pathname.startsWith("/orders") ||
            additionalRoutes.includes(location.pathname)
            : location.pathname === event.route ||
            additionalRoutes.includes(location.pathname);

          return (
            <div key={index}>
              <NavLink
                to={
                  isOrdersLink
                    ? (isManufacturerAdmin || isTeamMember || isCactusAdmin ? (userBrandRoute || "#") : (firstBrandRoute || "#"))
                    : event.route
                }
                className={({ isActive: linkActive }) =>
                  `w-[90%] rounded-tr-[20px] rounded-br-[20px] h-[40px] flex items-center justify-center no-underline cursor-pointer ${linkActive || isActive
                    ? "bg-[#DEE6F0]"
                    : "dashboard-links my-1 hover:bg-[#DEE6F0] hover:bg-opacity-10"
                  }`
                }
                onClick={(e) => {
                  if (isOrdersLink) {
                    if (!(isManufacturerAdmin || isTeamMember || isCactusAdmin) && event.subMenu) {
                      handleOrdersClick(e);
                    } else if (isManufacturerAdmin || isTeamMember || isCactusAdmin) {
                      const target = (userBrandRoute && userBrandRoute !== "#") ? userBrandRoute : firstBrandRoute;
                      if (target) {
                        e.preventDefault();
                        navigate(target);
                        setToggle(false);
                      }
                    } else {
                      setToggle(false);
                    }
                  } else {
                    setToggle(false);
                  }
                }}
              >
                <span className="w-[210px] ml-6 dashboard-links no-underline flex items-center">
                  {event.Icon ? (
                    <event.Icon color="#808693" aria-label={event.links} />
                  ) : (
                    <img src={event.img} alt={event.links} width={20} height={20} />
                  )}
                  <span className={`ml-2 whitespace-nowrap ${isActive ? "font-semibold" : ""}`}>{event.links}</span>
                </span>
              </NavLink>

              {/* Orders submenu */}
              {event.subMenu &&
                (isOrdersSubmenuOpen || additionalRoutes.includes(location.pathname)) && (
                  <div className="ml-16 mt-2 max-h-[200px] overflow-hidden">
                    <div className="h-full overflow-y-auto white-scrollbar">
                      {loading && !ordersSubmenu.length && (
                        <div className="text-sm text-gray-300 py-2">Loading manufacturers…</div>
                      )}
                      {error && (
                        <div className="text-sm text-red-300 py-2">Failed to load manufacturers.</div>
                      )}
                      {ordersSubmenu.map((subItem) => (
                        <NavLink
                          key={subItem.id}
                          to={subItem.route}
                          className={({ isActive }) =>
                            `rounded-full cursor-pointer w-[90%] h-[40px] no-underline flex items-center justify-center ${isActive ? "bg-[#DEE6F0]" : "dashboard-links my-1 hover:bg-[#DEE6F0]"
                            }`
                          }
                          onClick={() => setToggle(false)}
                        >
                          <span className="w-[210px] ml-6 dashboard-links no-underline flex items-center">
                            <img src={subItem.img} alt={subItem.links} />
                            <span className="ml-2 whitespace-nowrap hover:text-[#2B364B]">{subItem.links}</span>
                          </span>
                        </NavLink>
                      ))}
                      {!loading && !error && ordersSubmenu.length === 0 && (
                        <div className="text-sm text-gray-300 py-2">No manufacturers</div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MobileSideBar;
